import { type MaybeRef, computed, toRef, toValue } from "vue";

import * as Sentry from "@sentry/vue";
import { useMutation, useQuery } from "@tanstack/vue-query";

import { queryClient } from "@/query/queryClient";
import applicationsApiService from "@/services/modules/applications";
import metadataApiService from "@/services/modules/metadata";

import type { ApplicationMetadataSyncPayload } from "@/models/applications";
import type { Metadata, MetadataInput } from "@/schemas/metadata";

interface MetadataHookOptions {
  modelType: MaybeRef<string>;
  modelId: MaybeRef<string | undefined>;
}

export const useMetadataQuery = ({
  modelType,
  modelId,
  enabled = true,
}: MetadataHookOptions & {
  enabled?: MaybeRef<boolean>;
}) => {
  const modelTypeRef = toRef(modelType);
  const modelIdRef = toRef(modelId);

  return useQuery({
    queryKey: ["metadata", modelTypeRef, modelIdRef] as const,
    queryFn: async (): Promise<Metadata[]> => {
      return metadataApiService.list({
        model_type: toValue(modelType),
        model_id: modelIdRef.value!,
      });
    },
    enabled: computed(() => toValue(enabled) && !!toValue(modelId)),
  });
};

export const useSaveMetadataMutation = ({
  modelType,
  modelId,
}: MetadataHookOptions) => {
  return useMutation({
    mutationFn: async (
      params:
        | { mode: "create"; payload: MetadataInput }
        | { mode: "update"; metadataId: number; payload: MetadataInput }
    ): Promise<Metadata> => {
      if (params.mode === "update") {
        return metadataApiService.update({
          metadataId: params.metadataId,
          payload: params.payload,
        });
      }
      const id = toValue(modelId);
      if (!id) {
        const error = new Error("Create metadata called without model ID");
        Sentry.captureException(error);
        return Promise.reject(error);
      }
      return metadataApiService.create({
        model_type: toValue(modelType),
        model_id: id,
        ...params.payload,
      });
    },
    onSuccess: () => {
      const id = toValue(modelId);
      if (!id) {
        return;
      }
      queryClient.invalidateQueries({
        queryKey: ["metadata", toValue(modelType), id],
      });
    },
  });
};

export const useDeleteMetadataMutation = ({
  modelType,
  modelId,
}: MetadataHookOptions) => {
  return useMutation({
    mutationFn: async (metadataId: number): Promise<void> => {
      await metadataApiService.delete(metadataId);
    },
    onSuccess: () => {
      const id = toValue(modelId);
      if (!id) {
        return;
      }
      queryClient.invalidateQueries({
        queryKey: ["metadata", toValue(modelType), id],
      });
    },
  });
};

export const useSyncMetadataMutation = (
  applicationId: MaybeRef<string | undefined>
) => {
  return useMutation({
    mutationFn: async (
      payload: ApplicationMetadataSyncPayload
    ): Promise<void> => {
      const id = toValue(applicationId);
      if (!id) {
        const error = new Error("Sync metadata called without application ID");
        Sentry.captureException(error);
        return Promise.reject(error);
      }
      await applicationsApiService.syncMetadataWithFunders(id, payload);
    },
  });
};
